/**
 * Knowledge-base read-out progress, driven only by `item_answered` frames.
 *
 * The server reads the track's items one by one and closes each with one
 * `item_answered` frame (status `answered`, or `no_answer` when the rep stayed
 * silent through the check-in and the server moved on). Its `itemsAnswered`
 * count is authoritative. The local set of answered ids is only a fallback for
 * a frame that arrives without a usable count, and it dedupes by id because a
 * repeated frame for one item must never count twice ("7 out of 5 completed",
 * 2026-09-13).
 */
import type { ItemAnsweredData } from '../services/kbLiveFrames';

export interface KbItemProgress {
  answeredItemIds: ReadonlySet<string>;
  itemsAnswered: number;
  totalItems: number;
}

export const EMPTY_KB_ITEM_PROGRESS: KbItemProgress = {
  answeredItemIds: new Set<string>(),
  itemsAnswered: 0,
  totalItems: 0,
};

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

/** Fold one `item_answered` frame into the running progress. Pure. */
export function applyItemAnswered(
  previous: KbItemProgress,
  frame: Partial<ItemAnsweredData> | null | undefined,
): KbItemProgress {
  const answeredItemIds = new Set(previous.answeredItemIds);
  if (frame?.status === 'answered' && frame?.trackItemId) {
    answeredItemIds.add(frame.trackItemId);
  }
  const serverAnswered = frame?.itemsAnswered;
  const serverTotal = frame?.totalItems;
  return {
    answeredItemIds,
    itemsAnswered: isCount(serverAnswered) ? serverAnswered : answeredItemIds.size,
    totalItems: isCount(serverTotal) && serverTotal > 0 ? serverTotal : previous.totalItems,
  };
}

/** "N of M answered", or undefined before the item count is known. */
export function formatKbProgressLabel(itemsAnswered: number, totalItems: number): string | undefined {
  return totalItems > 0 ? `${itemsAnswered} of ${totalItems} answered` : undefined;
}

/** Whole-number share of the track's items the rep answered. */
export function kbCompletionPercent(itemsAnswered: number, totalItems: number): number {
  return totalItems > 0 ? Math.round((itemsAnswered / totalItems) * 100) : 0;
}
